// Commemorative delivery-deadline banner — client-side decision (cache-safe).
//
// Liquid serializes the delivery_promotion / delivery_deadline metaobjects into a
// JSON island and this module makes every time-dependent choice against the visitor's
// clock, so Shopify's full-page cache can never pin a stale "order by DD/MM". It is a
// faithful port of the React DeliveryDeadlineBanner (getActivePromotion → first
// matching deadline by order → hide once now >= endsAt), matching the store timezone
// (America/Sao_Paulo) exactly as the source's Intl formatter did.

const TIME_ZONE = 'America/Sao_Paulo';

// DD/MM in the store timezone — the Liquid emits endsAt as an absolute epoch, so
// formatting from that instant is timezone-unambiguous (mirrors utils.ts formatDeadlineDay).
function formatDeadlineDay(endsAtSeconds) {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: TIME_ZONE,
  }).format(new Date(endsAtSeconds * 1000));
}

// Pure selection — exported for unit tests. Returns the banner to render, or null.
// config: { promotions: [{id,title,startsAt,descriptionTemplate,note}],
//           deadlines:  [{promoId,position,handleMatch,endsAt,descriptionTemplate,titleOverride,hideNote}] }
export function selectBanner(config, handle, nowSeconds) {
  const promotions = (config && config.promotions) || [];
  const deadlines = (config && config.deadlines) || [];

  // Active promotion = the most recently started one whose start is in the past.
  const active = promotions
    .filter((promo) => promo.startsAt <= nowSeconds)
    .sort((left, right) => right.startsAt - left.startsAt)[0];
  if (!active) return null;

  // First matching deadline within that promotion — order matters (position),
  // so a handle that contains two substrings resolves to the earlier entry.
  const productHandle = handle || '';
  const entry = deadlines
    .filter((deadline) => deadline.promoId === active.id)
    .sort((left, right) => left.position - right.position)
    .find((deadline) => deadline.handleMatch && productHandle.includes(deadline.handleMatch));
  if (!entry) return null;

  // Past its deadline → no banner (no fallback to an older promotion).
  if (nowSeconds >= entry.endsAt) return null;

  const template = entry.descriptionTemplate || active.descriptionTemplate || '';
  return {
    title: entry.titleOverride || active.title,
    description: template.replace('{date}', formatDeadlineDay(entry.endsAt)),
    note: entry.hideNote ? null : active.note || null,
  };
}

// Legacy compat: the PDP Preact trees (product-b.liquid inline, pdp-configurator.js)
// still call deadlineSentence(banner, template). The banner now shows the real
// deadline copy from the metaobjects, so this simply returns banner.description.
export function deadlineSentence(banner) {
  return banner.description;
}

function fillBanner(root, banner) {
  const titleEl = root.querySelector('[data-ddb-title]');
  const descEl = root.querySelector('[data-ddb-desc]');
  const noteEl = root.querySelector('[data-ddb-note]');
  if (titleEl) titleEl.textContent = banner.title;
  if (descEl) descEl.textContent = banner.description;
  if (noteEl) {
    if (banner.note) {
      noteEl.textContent = banner.note;
      noteEl.hidden = false;
    } else {
      noteEl.hidden = true;
    }
  }
  root.hidden = false;
}

// The island is page-level (one product per page) so it can be read both by the shell
// below and by the PDP's Preact tree, which renders its own banner from the same config.
export function readConfig() {
  const island = document.querySelector('[data-ddb-config]');
  if (!island) return null;
  try {
    return JSON.parse(island.textContent);
  } catch {
    return null;
  }
}

function initRoot(root, config) {
  const banner = selectBanner(config, root.getAttribute('data-ddb-handle'), Date.now() / 1000);
  if (banner) fillBanner(root, banner);
}

function initAll() {
  const roots = document.querySelectorAll('[data-ddb]');
  if (!roots.length) return;
  const config = readConfig();
  if (!config) return;
  for (const root of roots) initRoot(root, config);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
}
